import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { AddCircleOutlineOutlined } from '@mui/icons-material';
import Swal from 'sweetalert2';

import { deleteRequest, getRequest, postRequest, putRequest } from 'Includes/api/apicall';
import { DEL_URL, GET_URL, POST_URL, PUT_URL } from 'Includes/urls';

function slugify(value) {
  const s = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return s.slice(0, 64) || 'type';
}

function TaskTypesSetup(props) {
  const [typesRows, setTypesRows] = React.useState([]);
  const [typesLoading, setTypesLoading] = React.useState(false);
  const [selectedTypeId, setSelectedTypeId] = React.useState('');
  const [stagesRows, setStagesRows] = React.useState([]);
  const [stagesLoading, setStagesLoading] = React.useState(false);

  const [typeDialog, setTypeDialog] = React.useState({ open: false, editing: null });
  const [typeForm, setTypeForm] = React.useState({ name: '', code: '' });

  const [stageDialog, setStageDialog] = React.useState({ open: false, editing: null });
  const [stageForm, setStageForm] = React.useState({ name: '', code: '', sort_order: 0 });
  const [bulkStageDialogOpen, setBulkStageDialogOpen] = React.useState(false);
  const [bulkStageText, setBulkStageText] = React.useState('');

  const loadTypes = React.useCallback(() => {
    setTypesLoading(true);
    getRequest(GET_URL.task_types.api, { limit: 200, pageno: 1 }, props)
      .then((response) => {
        if (response && response.status === 200) {
          setTypesRows(response.data?.data || []);
        }
      })
      .finally(() => setTypesLoading(false));
  }, [props]);

  React.useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  const loadStages = React.useCallback(() => {
    if (!selectedTypeId) {
      setStagesRows([]);
      return;
    }
    setStagesLoading(true);
    getRequest(
      GET_URL.task_type_stages.api,
      { limit: 500, pageno: 1, task_type: selectedTypeId },
      props
    )
      .then((response) => {
        if (response && response.status === 200) {
          setStagesRows(response.data?.data || []);
        }
      })
      .finally(() => setStagesLoading(false));
  }, [props, selectedTypeId]);

  React.useEffect(() => {
    loadStages();
  }, [loadStages]);

  const openCreateType = () => {
    setTypeForm({ name: '', code: '' });
    setTypeDialog({ open: true, editing: null });
  };

  const openEditType = (row) => {
    setTypeForm({ name: row.name || '', code: row.code || '' });
    setTypeDialog({ open: true, editing: row });
  };

  const saveType = () => {
    const name = typeForm.name.trim();
    if (!name) {
      Swal.fire({ icon: 'warning', title: 'Name is required' });
      return;
    }
    const code = (typeForm.code || '').trim() || slugify(name);
    const payload = { name, code, is_active: true };
    if (typeDialog.editing) {
      putRequest(`${PUT_URL.task_types.api}${typeDialog.editing.id}/`, payload, props).then((res) => {
        if (res?.status === 200) {
          Swal.fire({ icon: 'success', title: 'Saved', timer: 1000, showConfirmButton: false });
          setTypeDialog({ open: false, editing: null });
          loadTypes();
        }
      });
    } else {
      postRequest(POST_URL.task_types.api, [payload], props).then((res) => {
        if (res?.status === 200) {
          Swal.fire({ icon: 'success', title: 'Created', timer: 1000, showConfirmButton: false });
          setTypeDialog({ open: false, editing: null });
          loadTypes();
        }
      });
    }
  };

  const removeType = (row) => {
    deleteRequest(`${DEL_URL.task_types.api}${row.id}/`, {}, props).then((res) => {
      if (res?.status === 200) {
        Swal.fire({ icon: 'success', title: 'Deactivated', timer: 1000, showConfirmButton: false });
        if (String(selectedTypeId) === String(row.id)) setSelectedTypeId('');
        loadTypes();
      }
    });
  };

  const openCreateStage = () => {
    if (!selectedTypeId) return;
    const nextOrder = stagesRows.length
      ? Math.max(...stagesRows.map((r) => Number(r.sort_order) || 0)) + 1
      : 0;
    setStageForm({ name: '', code: '', sort_order: nextOrder });
    setStageDialog({ open: true, editing: null });
  };

  const openEditStage = (row) => {
    setStageForm({
      name: row.name || '',
      code: row.code || '',
      sort_order: row.sort_order ?? 0,
    });
    setStageDialog({ open: true, editing: row });
  };

  const saveStage = () => {
    const name = stageForm.name.trim();
    if (!name) {
      Swal.fire({ icon: 'warning', title: 'Stage name is required' });
      return;
    }
    const code = (stageForm.code || '').trim() || slugify(name);
    const payload = {
      name,
      code,
      sort_order: Number(stageForm.sort_order) || 0,
      task_type: Number(selectedTypeId),
      is_active: true,
    };
    if (stageDialog.editing) {
      putRequest(`${PUT_URL.task_type_stages.api}${stageDialog.editing.id}/`, payload, props).then((res) => {
        if (res?.status === 200) {
          Swal.fire({ icon: 'success', title: 'Saved', timer: 1000, showConfirmButton: false });
          setStageDialog({ open: false, editing: null });
          loadStages();
        }
      });
    } else {
      postRequest(POST_URL.task_type_stages.api, [payload], props).then((res) => {
        if (res?.status === 200) {
          Swal.fire({ icon: 'success', title: 'Created', timer: 1000, showConfirmButton: false });
          setStageDialog({ open: false, editing: null });
          loadStages();
        }
      });
    }
  };

  const removeStage = (row) => {
    deleteRequest(`${DEL_URL.task_type_stages.api}${row.id}/`, {}, props).then((res) => {
      if (res?.status === 200) {
        Swal.fire({ icon: 'success', title: 'Deactivated', timer: 1000, showConfirmButton: false });
        loadStages();
      }
    });
  };

  const openBulkStages = () => {
    if (!selectedTypeId) return;
    setBulkStageText('');
    setBulkStageDialogOpen(true);
  };

  const saveBulkStages = () => {
    const lines = bulkStageText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    if (!lines.length) {
      Swal.fire({ icon: 'warning', title: 'Enter at least one stage name' });
      return;
    }
    const existingCodes = new Set(stagesRows.map((r) => String(r.code || '').toLowerCase()));
    const orderBase = stagesRows.length
      ? Math.max(...stagesRows.map((r) => Number(r.sort_order) || 0)) + 1
      : 0;
    const payloads = [];
    lines.forEach((name, idx) => {
      let baseCode = slugify(name);
      let code = baseCode;
      let n = 2;
      while (existingCodes.has(code.toLowerCase())) {
        code = `${baseCode}_${n}`;
        n += 1;
      }
      existingCodes.add(code.toLowerCase());
      payloads.push({
        name,
        code,
        sort_order: orderBase + idx,
        task_type: Number(selectedTypeId),
        is_active: true,
      });
    });
    postRequest(POST_URL.task_type_stages.api, payloads, props).then((res) => {
      if (res?.status === 200) {
        Swal.fire({
          icon: 'success',
          title: `Created ${payloads.length} stage(s)`,
          timer: 1200,
          showConfirmButton: false,
        });
        setBulkStageDialogOpen(false);
        setBulkStageText('');
        loadStages();
      }
    });
  };

  const typeColumns = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 160 },
    { field: 'code', headerName: 'Code', flex: 0.8, minWidth: 120 },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={() => openEditType(params.row)}>
            Edit
          </Button>
          <Button size="small" color="error" variant="outlined" onClick={() => removeType(params.row)}>
            Deactivate
          </Button>
        </Box>
      ),
    },
  ];

  const stageColumns = [
    { field: 'sort_order', headerName: 'Order', width: 90 },
    { field: 'name', headerName: 'Stage', flex: 1, minWidth: 140 },
    { field: 'code', headerName: 'Code', flex: 0.8, minWidth: 120 },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={() => openEditStage(params.row)}>
            Edit
          </Button>
          <Button size="small" color="error" variant="outlined" onClick={() => removeStage(params.row)}>
            Deactivate
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700}>
        Task types & stages
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define workflow types and ordered Kanban stages for each type. Tasks can then be assigned to a type and stage.
      </Typography>

      <Paper sx={{ p: 2, borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Task types
          </Typography>
          <Button variant="contained" startIcon={<AddCircleOutlineOutlined />} onClick={openCreateType} sx={{ textTransform: 'none' }}>
            Add task type
          </Button>
        </Box>
        <DataGrid
          autoHeight
          rows={typesRows}
          columns={typeColumns}
          loading={typesLoading}
          disableRowSelectionOnClick
          getRowId={(r) => r.id}
          onRowClick={(params) => setSelectedTypeId(String(params.row.id))}
        />
      </Paper>

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          Stages for selected type
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="tts-type-label">Task type</InputLabel>
              <Select
                labelId="tts-type-label"
                label="Task type"
                value={selectedTypeId}
                onChange={(e) => setSelectedTypeId(e.target.value)}
              >
                <MenuItem value="">
                  <em>Select a task type</em>
                </MenuItem>
                {typesRows.map((t) => (
                  <MenuItem key={t.id} value={String(t.id)}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="contained" disabled={!selectedTypeId} onClick={openCreateStage} sx={{ textTransform: 'none' }}>
              Add stage
            </Button>
            <Button variant="outlined" disabled={!selectedTypeId} onClick={openBulkStages} sx={{ textTransform: 'none' }}>
              Add multiple stages
            </Button>
          </Grid>
        </Grid>
        {!selectedTypeId ? (
          <Typography variant="body2" color="text.secondary">
            Select a task type to manage its stages.
          </Typography>
        ) : (
          <DataGrid
            autoHeight
            rows={stagesRows}
            columns={stageColumns}
            loading={stagesLoading}
            disableRowSelectionOnClick
            getRowId={(r) => r.id}
          />
        )}
      </Paper>

      <Dialog open={typeDialog.open} onClose={() => setTypeDialog({ open: false, editing: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{typeDialog.editing ? 'Edit task type' : 'Add task type'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Name"
            size="small"
            value={typeForm.name}
            onChange={(e) => setTypeForm((p) => ({ ...p, name: e.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Code (unique)"
            size="small"
            helperText="Leave blank to auto-generate from name"
            value={typeForm.code}
            onChange={(e) => setTypeForm((p) => ({ ...p, code: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTypeDialog({ open: false, editing: null })}>Cancel</Button>
          <Button variant="contained" onClick={saveType}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkStageDialogOpen} onClose={() => setBulkStageDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add multiple stages</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            One stage name per line. Codes are generated from names; sort order continues after your current stages.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={6}
            label="Stage names"
            size="small"
            value={bulkStageText}
            onChange={(e) => setBulkStageText(e.target.value)}
            placeholder={'Development\nTesting\nDone'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkStageDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveBulkStages}>
            Create stages
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={stageDialog.open} onClose={() => setStageDialog({ open: false, editing: null })} maxWidth="sm" fullWidth>
        <DialogTitle>{stageDialog.editing ? 'Edit stage' : 'Add stage'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Stage name"
            size="small"
            value={stageForm.name}
            onChange={(e) => setStageForm((p) => ({ ...p, name: e.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Code"
            size="small"
            helperText="Leave blank to auto-generate from name"
            value={stageForm.code}
            onChange={(e) => setStageForm((p) => ({ ...p, code: e.target.value }))}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Sort order"
            type="number"
            size="small"
            value={stageForm.sort_order}
            onChange={(e) => setStageForm((p) => ({ ...p, sort_order: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStageDialog({ open: false, editing: null })}>Cancel</Button>
          <Button variant="contained" onClick={saveStage}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TaskTypesSetup;
